import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
  CForm,
  CFormLabel,
  CFormInput,
  CFormSelect,
  CFormTextarea,
  CTable,
  CTableHead,
  CTableBody,
  CTableHeaderCell,
  CTableDataCell,
  CTableRow,
} from "@coreui/react";
import { CIcon } from "@coreui/icons-react";
import { cilPlus, cilTrash, cilCopy, cilClipboard } from "@coreui/icons";

import "./MainNeuralModal.scss";

// Уникальный ID для строк
const generateId = () =>
  `row-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const AddCombined = ({
  isOpen,
  onClose,
  objectId,
  onAddSuccess,
  isEdit = false,
  editData = null,
  onEditSuccess = null,
}) => {
  const token = localStorage.getItem("token"); // ← Получаем токен

  const [firstLevelClasses, setFirstLevelClasses] = useState([]);
  const [secondLevelClasses, setSecondLevelClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    label: "",
    classIdFirstLevel: "",
    classIdSecondLevel: "",
    defectName: "",
    additionalInfo: "",
    quantitativeValue: "",
  });

  const [characterRows, setCharacterRows] = useState([
    { id: generateId(), value: "" },
  ]);
  const [recommendationRows, setRecommendationRows] = useState([
    { id: generateId(), value: "" },
  ]);
  const [causeRows, setCauseRows] = useState([{ id: generateId(), value: "" }]);

  const [selectedCharacterRowId, setSelectedCharacterRowId] = useState(null);
  const [selectedRecommendationRowId, setSelectedRecommendationRowId] =
    useState(null);
  const [selectedCauseRowId, setSelectedCauseRowId] = useState(null);

  const [clipboardRow, setClipboardRow] = useState(null);

  // Загрузка справочников
  const loadClasses = async () => {
    setLoading(true);
    setError("");
    try {
      const [firstRes, secondRes] = await Promise.all([
        axios.get("/api/neural_network_module/firstlevelclasses/", {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get("/api/neural_network_module/secondlevelclasses/", {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setFirstLevelClasses(Array.isArray(firstRes.data) ? firstRes.data : []);
      setSecondLevelClasses(
        Array.isArray(secondRes.data) ? secondRes.data : [],
      );
    } catch (err) {
      console.error("Ошибка загрузки классов:", err);
      const message =
        err.response?.data?.message ||
        err.message ||
        "Не удалось загрузить данные";
      setError(message);
      alert(`Ошибка: ${message}`);
    } finally {
      setLoading(false);
    }
  };

  // Инициализация при открытии
  useEffect(() => {
    if (isOpen || isEdit) {
      // Сброс формы
      if (!isEdit) {
        setFormData({
          label: "",
          classIdFirstLevel: "",
          classIdSecondLevel: "",
          defectName: "",
          additionalInfo: "",
          quantitativeValue: "",
        });
        setCharacterRows([{ id: generateId(), value: "" }]);
        setRecommendationRows([{ id: generateId(), value: "" }]);
        setCauseRows([{ id: generateId(), value: "" }]);
        setSelectedCharacterRowId(null);
        setSelectedRecommendationRowId(null);
        setSelectedCauseRowId(null);
      }

      // Заполнение при редактировании
      if (isEdit && editData) {
        try {
          setFormData({
            label: editData.Label || "",
            classIdFirstLevel: editData.ClassID_FirstLevel?.toString() || "",
            classIdSecondLevel: editData.ClassID_SecondLevel?.toString() || "",
            defectName: editData.DefectName || "",
            additionalInfo: editData.AdditionalInfo || "",
          });

          let parsedDC = null;
          if (editData.DC) {
            parsedDC = JSON.parse(editData.DC);
          }

          if (parsedDC && Array.isArray(parsedDC) && parsedDC.length > 0) {
            const details = parsedDC[0]?.Details || [];
            const qVal = parsedDC[0].QuantitativeValue || "";

            // Характер дефекта
            const charValues = parsedDC
              .map((item) => item.Name || "")
              .filter(Boolean);
            const newCharRows =
              charValues.length > 0
                ? charValues.map((val) => ({ id: generateId(), value: val }))
                : [{ id: generateId(), value: "" }];
            setCharacterRows(newCharRows);
            setSelectedCharacterRowId(newCharRows[0]?.id || null);

            // Рекомендации
            const recDetail = details.find((d) => d.Name === "Рекомендации");
            const recValues = recDetail?.Values || [];
            const newRecRows =
              recValues.length > 0
                ? recValues.map((val) => ({ id: generateId(), value: val }))
                : [{ id: generateId(), value: "" }];
            setRecommendationRows(newRecRows);
            setSelectedRecommendationRowId(newRecRows[0]?.id || null);

            // Причины
            const causeDetail = details.find((d) => d.Name === "Причины");
            const causeValues = causeDetail?.Values || [];
            const newCauseRows =
              causeValues.length > 0
                ? causeValues.map((val) => ({ id: generateId(), value: val }))
                : [{ id: generateId(), value: "" }];
            setCauseRows(newCauseRows);
            setSelectedCauseRowId(newCauseRows[0]?.id || null);
          } else {
            setCharacterRows([{ id: generateId(), value: "" }]);
            setRecommendationRows([{ id: generateId(), value: "" }]);
            setCauseRows([{ id: generateId(), value: "" }]);
            setSelectedCharacterRowId(null);
            setSelectedRecommendationRowId(null);
            setSelectedCauseRowId(null);
          }
        } catch (parseError) {
          console.error("Ошибка парсинга DC:", parseError);
          alert("Ошибка при загрузке данных для редактирования.");
        }
      }

      // Загрузка справочников при открытии модалки
      if (isOpen) {
        loadClasses();
      }
    }
  }, [isOpen, isEdit, editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Общие функции для строк
  const addRow = useCallback((rows, setRows, setSelectedId) => {
    const newRow = { id: generateId(), value: "" };
    const newRows = [...rows, newRow];
    setRows(newRows);
    setSelectedId(newRow.id);
  }, []);

  const removeRow = useCallback((rows, setRows, selectedId, setSelectedId) => {
    if (rows.length <= 1) return;
    const newRows = rows.filter((row) => row.id !== selectedId);
    setRows(newRows);
    setSelectedId(newRows.length > 0 ? newRows[newRows.length - 1].id : null);
  }, []);

  const copyRow = useCallback((selectedId, rows) => {
    const row = rows.find((r) => r.id === selectedId);
    if (row) setClipboardRow({ ...row });
  }, []);

  const pasteRow = useCallback(
    (targetRows, setTargetRows, setSelectedId) => {
      if (clipboardRow) {
        const pasted = { ...clipboardRow, id: generateId() };
        const newRows = [...targetRows, pasted];
        setTargetRows(newRows);
        setSelectedId(pasted.id);
      }
    },
    [clipboardRow],
  );

  const updateRow = useCallback((id, value, rows, setRows) => {
    setRows(rows.map((row) => (row.id === id ? { ...row, value } : row)));
  }, []);

  // Конкретные обработчики
  const addCharacterRow = () =>
    addRow(characterRows, setCharacterRows, setSelectedCharacterRowId);
  const removeCharacterRow = () =>
    removeRow(
      characterRows,
      setCharacterRows,
      selectedCharacterRowId,
      setSelectedCharacterRowId,
    );
  const copyCharacterRow = () => copyRow(selectedCharacterRowId, characterRows);
  const pasteCharacterRow = () =>
    pasteRow(characterRows, setCharacterRows, setSelectedCharacterRowId);
  const updateCharacterRow = (id, value) =>
    updateRow(id, value, characterRows, setCharacterRows);
  const handleCharacterRowClick = (id) => setSelectedCharacterRowId(id);

  const addRecommendationRow = () =>
    addRow(
      recommendationRows,
      setRecommendationRows,
      setSelectedRecommendationRowId,
    );
  const removeRecommendationRow = () =>
    removeRow(
      recommendationRows,
      setRecommendationRows,
      selectedRecommendationRowId,
      setSelectedRecommendationRowId,
    );
  const copyRecommendationRow = () =>
    copyRow(selectedRecommendationRowId, recommendationRows);
  const pasteRecommendationRow = () =>
    pasteRow(
      recommendationRows,
      setRecommendationRows,
      setSelectedRecommendationRowId,
    );
  const updateRecommendationRow = (id, value) =>
    updateRow(id, value, recommendationRows, setRecommendationRows);
  const handleRecommendationRowClick = (id) =>
    setSelectedRecommendationRowId(id);

  const addCauseRow = () =>
    addRow(causeRows, setCauseRows, setSelectedCauseRowId);
  const removeCauseRow = () =>
    removeRow(
      causeRows,
      setCauseRows,
      selectedCauseRowId,
      setSelectedCauseRowId,
    );
  const copyCauseRow = () => copyRow(selectedCauseRowId, causeRows);
  const pasteCauseRow = () =>
    pasteRow(causeRows, setCauseRows, setSelectedCauseRowId);
  const updateCauseRow = (id, value) =>
    updateRow(id, value, causeRows, setCauseRows);
  const handleCauseRowClick = (id) => setSelectedCauseRowId(id);

  // Отправка формы
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.label ||
      !formData.classIdFirstLevel ||
      !formData.classIdSecondLevel ||
      !formData.defectName
    ) {
      alert("Заполните все обязательные поля.");
      return;
    }

    if (!objectId && !editData?.ObjectID) {
      alert("Ошибка: Не указан ObjectID.");
      return;
    }

    // Формирование DC
    const dcData = [];
    const nonEmptyChars = characterRows
      .map((r) => r.value.trim())
      .filter(Boolean);
    const causes = causeRows.map((r) => r.value.trim()).filter(Boolean);
    const recommendations = recommendationRows
      .map((r) => r.value.trim())
      .filter(Boolean);

    if (nonEmptyChars.length > 0) {
      nonEmptyChars.forEach((char) => {
        dcData.push({
          Name: char,
          QuantitativeValue: formData.quantitativeValue || "",
          Details: [
            { Name: "Причины", Values: causes },
            { Name: "Рекомендации", Values: recommendations },
          ],
        });
      });
    }

    const payload = {
      ObjectID: objectId || editData.ObjectID,
      ClassID_FirstLevel: parseInt(formData.classIdFirstLevel, 10),
      ClassID_SecondLevel: parseInt(formData.classIdSecondLevel, 10),
      AdditionalInfo: formData.additionalInfo,
      Label: formData.label,
      DefectName: formData.defectName,
      DC: JSON.stringify(dcData),
    };

    // При редактировании — добавляем ID
    if (isEdit && editData?.ID) {
      payload.ID = editData.ID;
    }

    try {
      const url = "/api/neural_network_module/combinedtable/";
      const method = isEdit ? axios.put : axios.post;

      const response = await method(url, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        (isEdit ? onEditSuccess : onAddSuccess)?.();
        onClose();
      } else {
        alert(`Ошибка: статус ${response.status}`);
      }
    } catch (err) {
      console.error("Ошибка при сохранении:", err);
      const msg =
        err.response?.data?.message || err.response?.data?.error || err.message;
      alert(`Ошибка: ${msg}`);
    }
  };

  return (
    <CModal
      alignment="start"
      size="lg"
      visible={isOpen}
      onClose={onClose}
      backdrop="static"
    >
      <CModalHeader>
        <CModalTitle>
          {isEdit ? "Редактировать запись" : "Добавить запись"}
        </CModalTitle>
      </CModalHeader>

      <CModalBody>
        {error && <div className="alert alert-danger">{error}</div>}
        {loading && <p>Загрузка справочников...</p>}

        {!loading && (
          <CForm onSubmit={handleSubmit}>
            <div className="mb-3">
              <CFormLabel>Наименование метки:</CFormLabel>
              <CFormInput
                type="text"
                name="label"
                value={formData.label}
                onChange={handleChange}
                required
              />
            </div>

            <div className="mb-3">
              <CFormLabel>Метка-конструкция:</CFormLabel>
              <CFormSelect
                name="classIdFirstLevel"
                value={formData.classIdFirstLevel}
                onChange={handleChange}
                required
              >
                <option value="">Выберите метку-конструкции</option>
                {firstLevelClasses.length === 0 ? (
                  <option disabled>Нет данных</option>
                ) : (
                  firstLevelClasses.map((item) => (
                    <option key={item.ID} value={item.ID}>
                      {item.ClassID} - {item.NameOut}
                    </option>
                  ))
                )}
              </CFormSelect>
            </div>

            <div className="mb-3">
              <CFormLabel>Метка-дефекта:</CFormLabel>
              <CFormSelect
                name="classIdSecondLevel"
                value={formData.classIdSecondLevel}
                onChange={handleChange}
                required
              >
                <option value="">Выберите метку-дефекта</option>
                {secondLevelClasses.length === 0 ? (
                  <option disabled>Нет данных</option>
                ) : (
                  secondLevelClasses.map((item) => (
                    <option key={item.ID} value={item.ID}>
                      {item.ClassID} - {item.NameOut}
                    </option>
                  ))
                )}
              </CFormSelect>
            </div>

            <div className="mb-5">
              <CFormLabel>Наименование дефекта:</CFormLabel>
              <CFormInput
                type="text"
                name="defectName"
                value={formData.defectName}
                onChange={handleChange}
                required
              />
            </div>

            {/* Характер дефекта */}
            <div className="mb-4 table-wrapper">
              <div className="d-flex">
                <div className="table-controls me-2 d-flex flex-column">
                  <CButton
                    size="sm"
                    color="primary"
                    className="mb-1"
                    onClick={addCharacterRow}
                  >
                    <CIcon icon={cilPlus} />
                  </CButton>
                  <CButton
                    size="sm"
                    color="secondary"
                    className="mb-1"
                    onClick={removeCharacterRow}
                    disabled={characterRows.length <= 1}
                  >
                    <CIcon icon={cilTrash} />
                  </CButton>
                  <CButton
                    size="sm"
                    color="info"
                    className="mb-1"
                    onClick={copyCharacterRow}
                    disabled={!selectedCharacterRowId}
                  >
                    <CIcon icon={cilCopy} />
                  </CButton>
                  <CButton
                    size="sm"
                    color="success"
                    className="mb-1"
                    onClick={pasteCharacterRow}
                    disabled={!clipboardRow}
                  >
                    <CIcon icon={cilClipboard} />
                  </CButton>
                </div>
                <div className="flex-grow-1 table-container">
                  <CTable bordered small hover responsive>
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell>Характер дефекта</CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {characterRows.map((row) => (
                        <CTableRow
                          key={row.id}
                          onClick={() => handleCharacterRowClick(row.id)}
                          active={selectedCharacterRowId === row.id}
                          style={{ cursor: "pointer" }}
                        >
                          <CTableDataCell>
                            <CFormInput
                              type="text"
                              value={row.value}
                              onChange={(e) =>
                                updateCharacterRow(row.id, e.target.value)
                              }
                              size="sm"
                              className="w-100"
                            />
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>
                </div>
              </div>
            </div>

            {/* Рекомендации */}
            <div className="mb-4 table-wrapper">
              <div className="d-flex">
                <div className="table-controls me-2 d-flex flex-column">
                  <CButton
                    size="sm"
                    color="primary"
                    className="mb-1"
                    onClick={addRecommendationRow}
                  >
                    <CIcon icon={cilPlus} />
                  </CButton>
                  <CButton
                    size="sm"
                    color="secondary"
                    className="mb-1"
                    onClick={removeRecommendationRow}
                    disabled={recommendationRows.length <= 1}
                  >
                    <CIcon icon={cilTrash} />
                  </CButton>
                  <CButton
                    size="sm"
                    color="info"
                    className="mb-1"
                    onClick={copyRecommendationRow}
                    disabled={!selectedRecommendationRowId}
                  >
                    <CIcon icon={cilCopy} />
                  </CButton>
                  <CButton
                    size="sm"
                    color="success"
                    className="mb-1"
                    onClick={pasteRecommendationRow}
                    disabled={!clipboardRow}
                  >
                    <CIcon icon={cilClipboard} />
                  </CButton>
                </div>
                <div className="flex-grow-1 table-container">
                  <CTable bordered small hover responsive>
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell>
                          Рекомендации по устранению
                        </CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {recommendationRows.map((row) => (
                        <CTableRow
                          key={row.id}
                          onClick={() => handleRecommendationRowClick(row.id)}
                          active={selectedRecommendationRowId === row.id}
                          style={{ cursor: "pointer" }}
                        >
                          <CTableDataCell>
                            <CFormInput
                              type="text"
                              value={row.value}
                              onChange={(e) =>
                                updateRecommendationRow(row.id, e.target.value)
                              }
                              size="sm"
                              className="w-100"
                            />
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>
                </div>
              </div>
            </div>

            {/* Причины */}
            <div className="mb-4 table-wrapper">
              <div className="d-flex">
                <div className="table-controls me-2 d-flex flex-column">
                  <CButton
                    size="sm"
                    color="primary"
                    className="mb-1"
                    onClick={addCauseRow}
                  >
                    <CIcon icon={cilPlus} />
                  </CButton>
                  <CButton
                    size="sm"
                    color="secondary"
                    className="mb-1"
                    onClick={removeCauseRow}
                    disabled={causeRows.length <= 1}
                  >
                    <CIcon icon={cilTrash} />
                  </CButton>
                  <CButton
                    size="sm"
                    color="info"
                    className="mb-1"
                    onClick={copyCauseRow}
                    disabled={!selectedCauseRowId}
                  >
                    <CIcon icon={cilCopy} />
                  </CButton>
                  <CButton
                    size="sm"
                    color="success"
                    className="mb-1"
                    onClick={pasteCauseRow}
                    disabled={!clipboardRow}
                  >
                    <CIcon icon={cilClipboard} />
                  </CButton>
                </div>
                <div className="flex-grow-1 table-container">
                  <CTable bordered small hover responsive>
                    <CTableHead>
                      <CTableRow>
                        <CTableHeaderCell>
                          Причины возникновения
                        </CTableHeaderCell>
                      </CTableRow>
                    </CTableHead>
                    <CTableBody>
                      {causeRows.map((row) => (
                        <CTableRow
                          key={row.id}
                          onClick={() => handleCauseRowClick(row.id)}
                          active={selectedCauseRowId === row.id}
                          style={{ cursor: "pointer" }}
                        >
                          <CTableDataCell>
                            <CFormInput
                              type="text"
                              value={row.value}
                              onChange={(e) =>
                                updateCauseRow(row.id, e.target.value)
                              }
                              size="sm"
                              className="w-100"
                            />
                          </CTableDataCell>
                        </CTableRow>
                      ))}
                    </CTableBody>
                  </CTable>
                </div>
              </div>
            </div>

            <div className="mb-3">
              <CFormLabel>Нормативная ссылка:</CFormLabel>
              <CFormTextarea
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                rows={3}
              />
            </div>
          </CForm>
        )}
      </CModalBody>

      <CModalFooter>
        <CButton color="primary" onClick={handleSubmit} disabled={loading}>
          {isEdit ? "Сохранить" : "ОК"}
        </CButton>
        <CButton color="secondary" onClick={onClose} disabled={loading}>
          Отмена
        </CButton>
      </CModalFooter>

      <style jsx>{`
        .table-controls {
          min-width: 40px;
        }
        .table-controls .btn {
          padding: 0.25rem 0.5rem;
          font-size: 0.75rem;
        }
        .table-container {
          max-height: 200px;
          overflow-y: auto;
        }
        .table-container table {
          margin-bottom: 0;
          width: 100%;
        }
      `}</style>
    </CModal>
  );
};

export default AddCombined;
